// ============================================================
// MainframeShell — the Square Code platform shell.
//
//   ┌────────────────────────────────────────────────────────┐
//   │ TopBar   SQUARE CODE · panel switch · DEMO            │
//   ├────────┬───────────────────────────────┬───────────────┤
//   │Sidebar │                               │  RightPanel   │
//   │DASH    │      <Outlet/> (main col)     │  detail |     │
//   │CMD     │                               │  comms |      │
//   │OLV     │                               │  action       │
//   │DOM     │                               │               │
//   ├────────┴───────────────────────────────┴───────────────┤
//   │ CommandBar  Q  Computer, execute...   Execute          │
//   └────────────────────────────────────────────────────────┘
//
// A subtle SVG grid overlay runs behind everything to give the
// "mainframe substrate" atmosphere from the design reference.
// OLIVIUM is still one of four sidebar sections — never the brand.
// ============================================================

import { useEffect } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { ShellProvider } from './ShellContext'
import Sidebar from './Sidebar'
import TopBar from './TopBar'
import RightPanel from './RightPanel'
import CommandBar from './CommandBar'
import AssistantDock from './AssistantDock'
import MultimodalCommandDock from './MultimodalCommandDock'
import { GRID_BACKGROUND_URL, shellStyles } from './styles'
import './shell-layout.css'
import ShellErrorBoundary from '../observability/ShellErrorBoundary'
import { recordShellAction, setRoute } from '../observability/sentry'
import { WorkbenchProvider } from './workbench/WorkbenchContext'
import AgentWorkbench from './workbench/AgentWorkbench'

// Global Cmd/Ctrl+K → focus the command bar (it listens for sc-command-focus).
function CommandHotkey() {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault()
        window.dispatchEvent(new CustomEvent('sc-command-focus', { detail: {} }))
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])
  return null
}

export default function MainframeShell() {
  return (
    <ShellErrorBoundary>
      <ShellProvider>
       <WorkbenchProvider>
        <div style={shellStyles.app}>
          <CommandHotkey />
          {/* Ambient radial glow */}
          <div style={shellStyles.ambient} />
          {/* Fixed purple grid — the OLIVIUM frequency bleeding through.
              Brand Guide v2: 50px × 50px, drifts over 60s linear infinite. */}
          <div
            aria-hidden
            style={{
              position: 'fixed',
              inset: '-50px 0 0 0',
              pointerEvents: 'none',
              zIndex: 0,
              backgroundImage: GRID_BACKGROUND_URL,
              backgroundSize: '50px 50px',
              animation: 'sc-grid-drift 60s linear infinite',
            }}
          />

          <TopBar />

          <div style={shellStyles.body} className="sc-shell-body">
            <Sidebar />
            <div style={shellStyles.mainCol}>
              <main style={shellStyles.main} className="sc-shell-main">
                <RouteTracker />
                <Outlet />
              </main>
            </div>
            <RightPanel />
          </div>

          {/* Multimodal dock (v21) — collapsed by default; mode chips with
              route + permission preview. Nothing executes from the dock. */}
          <MultimodalCommandDock />

          <CommandBar />

          {/* Global assistant dock — text + navigation now, voice/AI in Phase 2. */}
          <AssistantDock />

          {/* Persistent Agent Workbench — dockable, survives navigation. */}
          <AgentWorkbench />
        </div>
       </WorkbenchProvider>
      </ShellProvider>
    </ShellErrorBoundary>
  )
}

function RouteTracker() {
  const location = useLocation()
  useEffect(() => {
    setRoute(location.pathname)
    recordShellAction('shell.transition', { route: location.pathname })
  }, [location.pathname])
  return null
}
